// @flow

import React, { useMemo, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Spinner, Tooltip, Position, Intent } from '@blueprintjs/core';

import CategorizedPill from '../pills/categorized-pill';
import CategorizedPickerPopover from '../pills/categorized-picker-popover';
import type {
  CategoryMeta,
  Option,
} from '../pills/categorized-picker-popover';

import {
  PREFERENCE_TYPES,
  findPreferenceTypeMeta,
} from './preference-types';
import type { UserProfilePermissionsT } from './use-user-profile-permissions';

import * as Selectors from '../../selectors';
import {
  createUserPreference,
  updateUserPreference,
  deleteUserPreference,
} from '../../actions/users';
import { errorToast, successToast } from '../../utils/toaster';
import * as UserListModel from '../../models/user-list';

type OwnProps = {
  userId: number,
  permissions: UserProfilePermissionsT,
};

type StateProps = {
  user: ?UserListModel.userT,
  preferences: Object[],
  locations: Object[],
  entityTags: Object,
  organisationId: ?string,
  usersLoading: boolean,
  embeddedIncludes: ?$ReadOnlyArray<string>,
};

type DispatchProps = {
  createPreference: (userId: number, body: Object) => Promise<?Object>,
  updatePreference: (
    userId: number,
    preferenceId: number,
    body: { preferenceValue: string },
  ) => Promise<?Object>,
  deletePreference: (userId: number, preferenceId: number) => Promise<boolean>,
};

type Props = OwnProps & StateProps & DispatchProps;

const Cell = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  padding: 4px 0;
`;

const Empty = styled.span`
  font-size: 11px;
  color: #a7b6c2;
  font-style: italic;
  margin-right: 4px;
`;

const FALLBACK_CATEGORY_COLOR = '#5c7080';

const PreferencesCell = (props: Props) => {
  const {
    userId,
    permissions,
    user,
    preferences,
    locations,
    entityTags,
    organisationId,
    usersLoading,
    embeddedIncludes,
    createPreference,
    updatePreference,
    deletePreference,
  } = props;

  const [inFlightDeleteIds, setInFlightDeleteIds] = useState<Set<number>>(
    new Set(),
  );

  const deps = useMemo(
    () => ({ locations, entityTags }),
    [locations, entityTags],
  );

  // True when at least one of this user's preferences references a slice
  // that's still being hydrated — drives the per-row spinner so a
  // not-yet-hydrated dependency doesn't flash a "not found" pill. The
  // condition is registry-driven; the cell does not inspect preferenceType.
  const hasPendingPref = useMemo(
    () =>
      (preferences || []).some((p) => {
        if (!p) return false;
        const meta = findPreferenceTypeMeta(p.preferenceType);
        if (!meta) return false;
        return meta.isResolutionPending(deps);
      }),
    [preferences, deps],
  );

  const categories: $ReadOnlyArray<CategoryMeta> = useMemo(
    () =>
      PREFERENCE_TYPES.map((meta) => ({
        key: meta.key,
        label: meta.label,
        color: meta.color || FALLBACK_CATEGORY_COLOR,
      })),
    [],
  );

  const options: $ReadOnlyArray<Option> = useMemo(
    () =>
      PREFERENCE_TYPES.flatMap((meta) =>
        meta.getOptions(deps).map((o) => ({
          id: o.id,
          label: o.label,
          categoryKey: meta.key,
          subTypeKey: o.subTypeKey,
          subTypeLabel: o.subTypeLabel,
          color: o.color,
        })),
      ),
    [deps],
  );

  const excludeIds = useMemo(
    () =>
      user
        ? PREFERENCE_TYPES.flatMap((meta) => meta.getUserValuesForType(user))
        : [],
    [user],
  );

  const singleSelectCategories = useMemo(
    () =>
      PREFERENCE_TYPES.filter((m) => m.cardinality === 'single').map(
        (m) => m.key,
      ),
    [],
  );

  const conflictsByCategory = useMemo(() => {
    const out: { [string]: ?{ valueLabel: string } } = {};
    PREFERENCE_TYPES.forEach((meta) => {
      if (meta.cardinality !== 'single') {
        out[meta.key] = null;
        return;
      }
      const existing = (preferences || []).find(
        (p) => p && p.preferenceType === meta.key,
      );
      if (!existing) {
        out[meta.key] = null;
        return;
      }
      const content = meta.buildPillContent(existing, deps);
      out[meta.key] = {
        valueLabel: content.missing ? 'not found' : content.valueLabel,
      };
    });
    return out;
  }, [preferences, deps]);

  const handleRemove = useCallback(
    async (prefId: number) => {
      setInFlightDeleteIds((prev) => {
        const next = new Set(prev);
        next.add(prefId);
        return next;
      });
      try {
        const ok = await deletePreference(userId, prefId);
        if (!ok) {
          errorToast({
            message: 'Could not remove preference.',
            intent: Intent.DANGER,
          });
        } else {
          successToast({
            message: 'Preference removed.',
            intent: Intent.SUCCESS,
          });
        }
      } finally {
        setInFlightDeleteIds((prev) => {
          const next = new Set(prev);
          next.delete(prefId);
          return next;
        });
      }
    },
    [deletePreference, userId],
  );

  const handlePick = useCallback(
    async (option: Option) => {
      const meta = findPreferenceTypeMeta(option.categoryKey);
      if (!meta) return;
      if (!meta.validateValue(option.id)) {
        errorToast({
          message: `Invalid value for ${meta.label}.`,
          intent: Intent.WARNING,
        });
        return;
      }
      if (!organisationId) {
        errorToast({
          message: 'Could not resolve organisation.',
          intent: Intent.WARNING,
        });
        return;
      }
      if (meta.cardinality === 'single') {
        const existing = (preferences || []).find(
          (p) => p && p.preferenceType === meta.key,
        );
        if (existing) {
          const result = await updatePreference(userId, existing.id, {
            preferenceValue: option.id,
          });
          if (!result) {
            errorToast({
              message: `Could not update ${meta.label}.`,
              intent: Intent.WARNING,
            });
          } else {
            successToast({
              message: 'Preference updated.',
              intent: Intent.SUCCESS,
            });
          }
          return;
        }
      }
      const result = await createPreference(userId, {
        orgId: organisationId,
        preferenceType: meta.key,
        preferenceValue: option.id,
      });
      if (!result) {
        errorToast({
          message: `Could not add ${meta.label}.`,
          intent: Intent.WARNING,
        });
      } else {
        successToast({
          message: 'Preference added.',
          intent: Intent.SUCCESS,
        });
      }
    },
    [createPreference, updatePreference, userId, preferences, organisationId],
  );

  // Per-row loading: while users for this status are loading and the row
  // hasn't materialised yet, or a preference's resolution slice is still
  // hydrating (would otherwise flash "not found").
  if (
    (usersLoading && (!user || !Array.isArray(user.preferences))) ||
    hasPendingPref
  ) {
    return (
      <Cell onClick={(e) => e.stopPropagation()}>
        <Spinner size={14} />
      </Cell>
    );
  }

  // Per-include 403 fallback: the row exists but `preferences` was never
  // embedded for this user's status — show empty with a "no permission"
  // tooltip and no edit affordances.
  const preferencesNotEmbedded =
    Array.isArray(embeddedIncludes) &&
    !embeddedIncludes.includes('preferences');
  if (preferencesNotEmbedded) {
    return (
      <Cell onClick={(e) => e.stopPropagation()}>
        <Tooltip
          content="You do not have permission to view preferences for these users"
          position={Position.TOP}
        >
          <Empty>—</Empty>
        </Tooltip>
      </Cell>
    );
  }

  // Sorted pill iteration — registry order across types, then
  // (typeLabel, valueLabel) alphabetical within a type.
  const builtByType: Array<{
    metaKey: string,
    items: Array<{
      preference: Object,
      content: {
        typeLabel: string,
        valueLabel: string,
        color: string,
        missing: boolean,
      },
    }>,
  }> = PREFERENCE_TYPES.map((meta) => {
    const items = (preferences || [])
      .filter((p) => p && p.preferenceType === meta.key)
      .map((preference) => ({
        preference,
        content: meta.buildPillContent(preference, deps),
      }))
      .sort((a, b) => {
        const t = a.content.typeLabel.localeCompare(b.content.typeLabel);
        if (t !== 0) return t;
        return a.content.valueLabel.localeCompare(b.content.valueLabel);
      });
    return { metaKey: meta.key, items };
  });

  // Defensive: render any unknown-type preferences with a fallback grey pill,
  // so a future auth-service type doesn't crash the cell.
  const unknownPrefs = (preferences || []).filter(
    (p) => p && !findPreferenceTypeMeta(p.preferenceType),
  );

  const totalPills =
    builtByType.reduce((acc, g) => acc + g.items.length, 0) +
    unknownPrefs.length;

  const renderRemove = permissions && permissions.canDeleteUserProfile;
  const showAdd = permissions && permissions.canAddPreference;

  if (totalPills === 0 && !showAdd) {
    return (
      <Cell onClick={(e) => e.stopPropagation()}>
        <Empty>No preferences</Empty>
      </Cell>
    );
  }

  return (
    <Cell onClick={(e) => e.stopPropagation()}>
      {builtByType.map((g) =>
        g.items.map(({ preference, content }) => {
          const inFlight = inFlightDeleteIds.has(preference.id);
          // Suppress the X entirely while a DELETE is in flight. Passing
          // a no-op would leave the button visually enabled, which is
          // confusing; absence of the X matches the location-tagging
          // precedent and avoids the appearance of an interactive control.
          const onRemove =
            renderRemove && !inFlight
              ? () => handleRemove(preference.id)
              : undefined;
          return (
            <CategorizedPill
              key={`pref-${preference.id}`}
              typeLabel={content.typeLabel}
              valueLabel={content.missing ? 'not found' : content.valueLabel}
              color={content.color}
              missing={content.missing}
              size="sm"
              onRemove={onRemove}
              removeTooltip={`Remove ${content.typeLabel}: ${content.missing ? 'not found' : content.valueLabel} from this user`}
            />
          );
        }),
      )}
      {unknownPrefs.map((p) => (
        <CategorizedPill
          key={`pref-unknown-${p.id}`}
          typeLabel={p.preferenceType || 'Unknown'}
          valueLabel={String(p.preferenceValue || '')}
          color={FALLBACK_CATEGORY_COLOR}
          missing
          size="sm"
        />
      ))}
      {showAdd && (
        <CategorizedPickerPopover
          categories={categories}
          options={options}
          excludeIds={excludeIds}
          singleSelectCategories={singleSelectCategories}
          conflictsByCategory={conflictsByCategory}
          size="sm"
          addLabel="Add preference"
          categoryHeaderLabel="Choose preference type"
          onPick={handlePick}
        />
      )}
    </Cell>
  );
};

const mapStateToProps = (state, ownProps: OwnProps): StateProps => {
  const { userId } = ownProps;
  const user = Selectors.getUserById(state, userId);
  const status = user ? user.userListType : null;
  const userModel = Selectors.getUserModel(state);
  return {
    user,
    preferences: Selectors.getPreferencesForUser(state, userId),
    locations: Selectors.getLocations(state),
    entityTags: Selectors.getEntityTagsState(state),
    organisationId: userModel ? userModel.organisationId : null,
    usersLoading: status ? Selectors.getUsersLoading(state, status) : false,
    embeddedIncludes: status
      ? Selectors.getEmbeddedIncludes(state, status)
      : null,
  };
};

const mapDispatchToProps = {
  createPreference: createUserPreference,
  updatePreference: updateUserPreference,
  deletePreference: deleteUserPreference,
};

export default connect(mapStateToProps, mapDispatchToProps)(PreferencesCell);
